/**
 * How a segment's counts are shown. This is the point of slice 2, so it is a
 * component rather than four numbers copied onto three screens.
 *
 * A segment never reports one number, and four bare figures are not much better
 * than one: the gap between them is the information. What a person has to come
 * away knowing is
 *
 *   this segment holds 1,287 rows, but only 1,278 of them are different people,
 *   because nine rows are someone who is already in it — and of those people,
 *   526 can be reached by email and 752 by WhatsApp, so what a campaign reaches
 *   depends on the channel it picks.
 *
 * so the panel writes that out in words beside the figures. The gap has to read
 * as expected precision, not as a bug: a campaign sends to the de-duplicated
 * set, and this is the screen where that is explained, long before anyone is
 * standing in front of a send button wondering why the number moved.
 *
 * Two things this file must never do: cache a count (they are computed on every
 * read, because a stored one drifts the moment a list is re-imported), and show
 * a reachability figure against `contacts`. The reachability counts are counts
 * of **distinct addresses** — the number a campaign on that channel will
 * actually deliver — so they belong beside `uniqueRecipients`, which is the
 * other count of distinct things. Putting "526 of 1,287" on screen would invite
 * exactly the arithmetic this panel exists to prevent, and the 41% it produces
 * is not a share of anything.
 *
 * ## Slice 66: the Blueprint result panel, and what it was not allowed to bring
 *
 * The panels are `MetricPanel`s now — the concept's "matches now" card: one
 * headline figure, what it is out of in the caller's words, then ruled
 * sections. Three choices in that port are deliberate and each one is a place
 * the concept would have said something false here:
 *
 * - **The headline is `uniqueRecipients`, and its line says "from N contacts
 *   (rows)", not "of".** People and rows are different units; "1,284 of 1,290"
 *   reads as a share, and a share of rows is not what 1,284 is. For the same
 *   reason the headline carries **no meter** — the concept's bar under "595
 *   people" is a share of profiles in scope, and there is no such denominator
 *   here. A bar of people over rows would be the 41%-of-nothing above, drawn.
 * - **Reach by channel is `BySourceBars`, out of `uniqueRecipients`.** Each row
 *   says "526 of 1,284" — distinct addresses against distinct people, the one
 *   comparison these numbers support. The rows are counts, never pooled into a
 *   rate across channels (`BySourceBars`' own header says why that matters
 *   here of all places). The old per-row "· 41%" went with the old bar: the
 *   count beside its denominator says it without rounding.
 * - **Counts are sans with tabular figures, no longer mono.** Mono's dotted
 *   zero reads as an 8 at the size these are drawn, and a misread digit in a
 *   send size is the expensive kind.
 *
 * The emphasis gradient on the old headline figure is gone with it: colour on a
 * figure reads as a verdict, and this one is a count.
 *
 * `ProjectedCountsPanel` adds one thing the concept had and this screen lacked:
 * a bar per ticked list, so a person can see which list the rows come from.
 * Those rows are uploads, which is the one identity colour with a consumer
 * today (`SOURCE_KIND_TONE.upload`). A list that has not imported anything
 * shows the no-figure dash and its own word — "none yet", or "none" for a
 * failed import — never a `0` that would read as an empty list.
 */
import type { SegmentCounts, SegmentDataset } from '@wend/shared';
import {
  BySourceBars,
  Callout,
  MetricPanel,
  MetricSection,
  SOURCE_KIND_TONE,
  type BySourceRow,
} from '@wend/ui';
import type { ReactNode } from 'react';

import { formatCount, pluralise } from '../lib/format';
import {
  SEGMENT_COUNT_LABEL,
  SEGMENT_COUNT_MEANING,
  readSegmentCounts,
} from '../lib/segment-display';

/* -------------------------------------------------------------------------- */
/* The explanations                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Why `contacts` and `uniqueRecipients` differ — or why they do not.
 *
 * Written as a claim about people rather than about rows, because that is the
 * question being answered: how many messages will this actually send.
 */
function DeduplicationNote({ counts }: { counts: SegmentCounts }) {
  const { duplicateRows, empty } = readSegmentCounts(counts);

  if (empty) {
    return (
      <p className="max-w-prose text-[0.86rem] leading-relaxed text-muted">
        There is nothing in this segment yet, so every count is zero. Add a list that has finished
        importing and the numbers fill in — they are counted from the lists each time this page
        loads, never stored, so they cannot go stale.
      </p>
    );
  }

  if (duplicateRows === 0) {
    return (
      <p className="max-w-prose text-[0.86rem] leading-relaxed text-muted">
        Every row here is a different person.
        {/* Until slice 58 this said "no email address or phone number appears
            twice here, so every row is a different person and a campaign would
            send N messages". Since people are stored, neither half holds: two
            people can share a phone number (a branch's office line), and a
            campaign sends only to those its channel can reach. The fact is the
            first five words. */}
      </p>
    );
  }

  return (
    <p className="max-w-prose text-[0.86rem] leading-relaxed text-muted">
      <span className="text-text">
        {pluralise(duplicateRows, 'of these rows is', 'of these rows are')}
      </span>{' '}
      someone the segment already contains, so it holds{' '}
      <span className="text-text tabular-nums">{formatCount(counts.uniqueRecipients)}</span> people,
      not <span className="tabular-nums">{formatCount(counts.contacts)}</span>.
      {/* A second paragraph here explained the de-duplication as "on the email
          address, falling back to the phone number … Nothing is merged". Slice 58
          made both false — people are stored, and two uploads of one person are
          merged into one — and a sentence about the mechanism had no place on a
          product surface anyway (slice 38). The rows are still never changed:
          each stays in the list it came from. */}
    </p>
  );
}

/** What the two reachability figures do and do not promise. */
function ReachNote({ counts }: { counts: SegmentCounts }) {
  const { bothChannels, channelsDisjoint } = readSegmentCounts(counts);

  const overlap = channelsDisjoint ? (
    <>
      {' '}
      Nobody here has both, so the two channels reach two different groups and neither one touches
      the other.
    </>
  ) : bothChannels > 0 ? (
    <>
      {' '}
      <span className="text-text">{pluralise(bothChannels, 'person', 'people')}</span>{' '}
      {bothChannels === 1 ? 'has' : 'have'} both, so either channel reaches them.
    </>
  ) : null;

  return (
    <p className="mt-3 max-w-prose text-[0.82rem] leading-relaxed text-muted">
      Nobody is counted twice: this is the number a campaign on that channel sends to.{overlap} A campaign
      picks one channel and reaches only the people who can be reached that way.
      {/* The tail — *"Wend never falls back to the other one to make a number
          look bigger, because that would message someone on a channel they were
          never chosen for"* — was product policy phrased as a defence against an
          accusation nobody has made. The clause before it is the fact. Slice 29. */}
    </p>
  );
}

/**
 * The two channel rows, each out of the people in the segment.
 *
 * `of` is `uniqueRecipients` on both rows — distinct addresses against distinct
 * people — and never `contacts`; the file header says why. The label is the
 * channel and the caption under the bars is `SEGMENT_COUNT_MEANING`'s, so the
 * words cannot drift from the wizard that shows the same figure at send time.
 */
function reachRows(counts: SegmentCounts): BySourceRow[] {
  return [
    {
      key: 'email',
      label: SEGMENT_COUNT_LABEL.emailReachable,
      count: counts.emailReachable,
      of: counts.uniqueRecipients,
    },
    {
      key: 'whatsapp',
      label: SEGMENT_COUNT_LABEL.phoneReachable,
      count: counts.phoneReachable,
      of: counts.uniqueRecipients,
    },
  ];
}

/* -------------------------------------------------------------------------- */
/* The panel                                                                   */
/* -------------------------------------------------------------------------- */

export interface SegmentCountsPanelProps {
  counts: SegmentCounts;
  className?: string;
  /** Further `MetricSection`s, drawn inside the panel after the reach rows. */
  children?: ReactNode;
}

/**
 * The three counts, prominently and explained. Used wherever a segment's real
 * numbers are shown — never for a projection, which cannot know two of them.
 */
export function SegmentCountsPanel({ counts, className, children }: SegmentCountsPanelProps) {
  const { empty } = readSegmentCounts(counts);

  return (
    <MetricPanel
      className={className}
      kicker={SEGMENT_COUNT_LABEL.uniqueRecipients}
      figure={{ kind: 'measured', value: counts.uniqueRecipients, unit: 'people' }}
      sub={
        <>
          {SEGMENT_COUNT_MEANING.uniqueRecipients}, from{' '}
          <span className="text-text tabular-nums">{formatCount(counts.contacts)}</span>{' '}
          {SEGMENT_COUNT_LABEL.contacts.toLowerCase()} (rows)
        </>
      }
    >
      <div className="mt-3">
        <DeduplicationNote counts={counts} />
      </div>

      <MetricSection kicker="Reachable by channel">
        {empty ? (
          <p className="max-w-prose text-[0.86rem] leading-relaxed text-muted">Nothing to reach yet.</p>
        ) : (
          <>
            <BySourceBars rows={reachRows(counts)} />
            <ReachNote counts={counts} />
          </>
        )}
      </MetricSection>

      {children}
    </MetricPanel>
  );
}

/* -------------------------------------------------------------------------- */
/* The projection, before a segment exists                                     */
/* -------------------------------------------------------------------------- */

export interface ProjectedCountsPanelProps {
  /** Rows the ticked lists hold between them, summed from their live counts. */
  contacts: number;
  /** How many lists are ticked. */
  datasetCount: number;
  /** True when a ticked list has not finished importing and contributes nothing yet. */
  hasUnimported?: boolean;
  /**
   * The ticked lists themselves, for the per-list bars. Optional so a caller
   * with only the totals still gets the figure; omitted, the bars are not drawn.
   */
  selected?: readonly SegmentDataset[];
  /**
   * The kicker's element. The create screen passes `'h2'`: the panel replaced
   * a Card whose `CardTitle` was an h2, and without it the projection dropped
   * out of the page's heading outline (slice 66 review).
   */
  kickerAs?: 'p' | 'h2';
  className?: string;
}

/**
 * One ticked list as a bar row.
 *
 * A list that is importing contributes the rows it has landed so far — the
 * same `contactCount` the total above sums — so its row shows that count and
 * says "so far". A list that has imported nothing has no count to show: the
 * row draws the no-figure dash and the picker's own word for why, never a `0`.
 *
 * The dash's screen-reader sentence is set here too. Left to its default it
 * said "Not measured — this is an absence, not a zero" beside a visible
 * "none yet": but a list that has imported nothing has not gone unmeasured —
 * it is known to hold nothing yet (slice 66 review).
 */
const LIST_ABSENT_MEANING: Readonly<Partial<Record<SegmentDataset['status'], string>>> = {
  pending: 'Nothing imported from this list yet — its columns are waiting to be confirmed.',
  failed: 'This list’s import failed, so nothing from it is counted.',
};

function listRow(dataset: SegmentDataset): BySourceRow {
  const counting = dataset.status === 'ready' || dataset.status === 'processing';
  const word =
    dataset.status === 'processing' ? 'so far' : dataset.status === 'failed' ? 'none' : dataset.status === 'pending' ? 'none yet' : null;

  return {
    key: dataset.id,
    tone: SOURCE_KIND_TONE.upload,
    label: (
      <>
        {dataset.name}
        {word !== null && <span className="text-muted"> · {word}</span>}
      </>
    ),
    count: counting ? dataset.contactCount : null,
    absent: { state: 'not_measured', meaning: LIST_ABSENT_MEANING[dataset.status] },
  };
}

/** The callout's title: "not finished importing" is false of a list whose import failed. */
function unimportedTitle(selected: readonly SegmentDataset[] | undefined): string {
  const statuses = new Set((selected ?? []).map((dataset) => dataset.status));
  return statuses.has('failed') && !statuses.has('processing') && !statuses.has('pending')
    ? 'A ticked list did not import'
    : 'A ticked list has not finished importing';
}

/**
 * One sentence per state a ticked list is in, in a fixed order. Without the
 * lists (a caller with only the totals), the one sentence true of every case.
 */
function unimportedSentences(selected: readonly SegmentDataset[] | undefined): string[] {
  if (selected === undefined) return ['Only rows a list has already imported are counted here.'];
  const statuses = new Set(selected.map((dataset) => dataset.status));
  const sentences: string[] = [];
  if (statuses.has('processing')) {
    sentences.push(
      'A list still importing counts the rows it has imported so far; the rest join when the import finishes.',
    );
  }
  if (statuses.has('pending')) {
    sentences.push('A list waiting for its columns to be confirmed adds nothing until it is imported.');
  }
  if (statuses.has('failed')) {
    sentences.push('A list whose import failed adds nothing unless it is imported again.');
  }
  return sentences;
}

/**
 * The live preview on the create screen.
 *
 * It shows the one number that can be known before saving — the size of the
 * union, which is just the ticked lists added up — and refuses to invent the
 * other two. Whether two of these rows are the same person depends on the
 * addresses inside them, not on how many rows there are, so a client-side guess
 * at the recipient count would be a fabrication of exactly the kind this slice
 * exists to prevent. Saving computes them from the contacts themselves, and the
 * segment lands on its own screen with all three.
 *
 * The figure is labelled in **rows**, and its line says "before duplicates" —
 * the brand panel makes this the biggest number on the screen, which is the
 * moment it most needs to say what it is not.
 */
export function ProjectedCountsPanel({
  contacts,
  datasetCount,
  hasUnimported = false,
  selected,
  kickerAs = 'p',
  className,
}: ProjectedCountsPanelProps) {
  if (datasetCount === 0) {
    // A true zero — an empty segment does report zeros — so the figure is
    // drawn, and the sentence says why it is zero and that saving it is fine.
    return (
      <MetricPanel
        className={className}
        kicker="What this will contain"
        kickerAs={kickerAs}
        figure={{ kind: 'measured', value: 0, unit: 'rows' }}
        figureSize="lg"
        sub="Nothing ticked yet"
      >
        <p className="mt-3 max-w-prose text-[0.86rem] leading-relaxed text-muted">
          Tick a list and its contacts are counted here. You can also save an empty segment now and
          choose its lists later — it will simply report zeros until you do.
        </p>
      </MetricPanel>
    );
  }

  return (
    <MetricPanel
      className={className}
      kicker="What this will contain"
      kickerAs={kickerAs}
      figure={{ kind: 'measured', value: contacts, unit: 'rows' }}
      figureSize="lg"
      sub={`Across the ${pluralise(datasetCount, 'list')} you have ticked, before duplicates`}
    >
      {selected !== undefined && selected.length > 0 && (
        <MetricSection kicker="By list">
          <BySourceBars rows={selected.map(listRow)} />
        </MetricSection>
      )}

      <div className="mt-4 flex flex-col gap-3">
        {/*
          SLICE 29: FIFTY-FOUR WORDS DEFENDING A DESIGN DECISION.

          This paragraph argued why Wend refuses to estimate the other two counts
          — that de-duplication depends on the addresses inside the rows rather
          than on how many rows there are. That is this file's own header
          argument, reprinted on screen. The sender needs the consequence, and
          the paragraph below already gives it: expect the recipient count at or
          below this number, because somebody on two lists is counted once.
        */}
        <p className="text-[0.86rem] leading-relaxed text-muted">
          Expect the recipient count to come out at or below {formatCount(contacts)} — a person who
          appears on two of these lists is counted once when a campaign sends.
        </p>
        {/*
          SLICE 66: THE WARNING IS A CALLOUT, AND IT SAYS WHAT IS TRUE OF EACH LIST.

          It used to say an unfinished list "contributes nothing to this total
          yet". True of a list awaiting its mapping; false of one mid-import,
          whose rows-so-far are already in `contactCount` and so in the figure
          above. The first slice 66 rewrite said "only the rows it has imported
          so far are counted; the rest join when the import finishes" — true of
          an import in progress, false of the other two: a failed import never
          finishes, and a list awaiting its mapping joins only after somebody
          confirms its columns (review). There are three states, so there are
          three sentences, and the callout prints the ones the ticked lists are
          in. `unimportedSentences` says which.
        */}
        {hasUnimported && (
          <Callout tone="warn" density="compact" title={unimportedTitle(selected)}>
            {unimportedSentences(selected).join(' ')}
          </Callout>
        )}
      </div>
    </MetricPanel>
  );
}

/* -------------------------------------------------------------------------- */
/* Compact forms, for a table row                                              */
/* -------------------------------------------------------------------------- */

/**
 * Both people-counts in one cell. Never one without the other: the list is
 * exactly where someone forms an expectation about how big a send will be.
 */
export function ContactsAndRecipients({ counts }: { counts: SegmentCounts }) {
  return (
    <div>
      <p className="text-[0.86rem] tabular-nums text-text">
        {formatCount(counts.contacts)} <span className="text-muted">contacts</span>
      </p>
      <p className="text-[0.74rem] tabular-nums text-muted">
        {formatCount(counts.uniqueRecipients)} unique
      </p>
    </div>
  );
}

/**
 * The two reachability figures in one cell, as two small bars — never as one
 * total. Each is out of the segment's unique recipients, as on the detail
 * screen, so the list and the segment draw the same comparison.
 */
export function ChannelReach({ counts }: { counts: SegmentCounts }) {
  if (counts.contacts === 0) {
    return <span className="text-muted">—</span>;
  }

  return <BySourceBars rows={reachRows(counts)} className="text-[0.76rem]" />;
}
